import { globalConfig } from './configuration/config';
import { io, Socket } from 'socket.io-client';
import { entityPool } from './core/engine';
import { Project, Space, Shot } from './app/entities';
import { Toast } from './features/toasts/models/toast';
import { toastPool } from './features/toasts/models/toast-pool';

export class AppSocket {
  private socket: Socket;

  async init(spaceId: string) {
    if (this.socket) {
      this.socket.disconnect();
    }

    const wsHost = globalConfig.config.webSocketHost;
    const wsUrl = wsHost.startsWith('http://') || wsHost.startsWith('https://')
      ? wsHost
      : `${window.location.protocol === 'https:' ? 'https://' : 'http://'}${wsHost}`;

    // Resolve the current access token via the api client (which AuthKit
    // owns the cache/refresh for). Sockets may 401 in environments where
    // the api's socket auth hasn't been migrated yet — that's a known
    // follow-up.
    const { api } = await import('./api');
    const token = await api.getAccessToken();

    this.socket = io(wsUrl, {
      auth: {
        token,
      },
      extraHeaders: {
        'x-space-id': spaceId,
      },
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 3,
      forceNew: false,
      withCredentials: false,
    });

    this.setupListeners();

    this.socket.on('connect_error', (error) => {
      console.log('connect_error', error);
    });
  }

  private setupListeners() {
    this.socket.on('project-created', (project: Project) => {
      const newProject = Object.assign(new Project(), project);
      entityPool.insert(newProject);
    });

    this.socket.on(
      'project-updated',
      ({ avatar, ...update }: Partial<Project> & { _id: string }) => {
        if (avatar) {
          // updated the avatar, so that, after receiving the socket response, it gets rerendered in the CoverImage component
          (update as any).avatar = `${avatar}&key=${Date.now()}`;
        }

        entityPool.updateEntity(update._id, update);
      },
    );

    this.socket.on('project-deleted', ({ projectId }: { projectId: string }) => {
      const project = entityPool.getEntity<Project>(projectId);
      if (!project) return;
      entityPool.delete(project);
    });

    this.socket.on(
      'credits.added',
      ({ spaceId, current }: { spaceId: string; current: number }) => {
        const space = Space.getOne(spaceId);
        if (!space) return;

        space.credits.current = current;

        // Update the space in the entity pool
        entityPool.updateEntity(spaceId, space);

        // Optionally, you can show a toast notification
        toastPool.insert(
          new Toast(`Credits added successfully, new credits are ${space.credits}`, 'success'),
        );
      },
    );

    this.socket.on('space-updated', ({ avatar, ...update }: Partial<Space> & { _id: string }) => {
      if (avatar) {
        // updated the avatar, so that, after receiving the socket response, it gets rerendered in the CoverImage component
        (update as any).avatar = `${avatar}&key=${Date.now()}`;
      }

      entityPool.updateEntity(update._id, update);
    });

    this.socket.on(
      'files-duplicate',
      ({ projectId, finishedCopying }: { projectId: string; finishedCopying: boolean }) => {
        const project = entityPool.getEntity<Project>(projectId);
        if (!project) return;

        project.finishedCopying = finishedCopying;

        if (finishedCopying) {
          const space = Space.getOne(project.spaceId);
          if (space) {
            space.storage.current += project?.usedStorage || 0;
          }
        }

        const message =
          project.finishedCopying === false
            ? 'Project failed to duplicate'
            : 'Project duplicated successfully';
        const type = project.finishedCopying === false ? 'error' : 'success';

        toastPool.insert(new Toast(message, type));

        if (finishedCopying === false) {
          entityPool.delete(project);
        }
      },
    );

    this.socket.on(
      'shot-cover-generated',
      ({
        shotId,
        taskStatus,
        cover,
        progress,
      }: {
        shotId: string;
        taskStatus: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PENDING' | 'CANCELLED';
        cover: Shot['cover'];
        progress: number;
      }) => {
        const shot = entityPool.getEntity<Shot>(shotId);
        if (!shot) return;

        if (taskStatus === 'PENDING') {
          shot.isCoverGenerated = false;
          shot.coverGenerateProgress = 0;
          return;
        }

        if (taskStatus === 'RUNNING') {
          shot.coverGenerateProgress = Math.round(progress || 0);
          return;
        }

        let message = 'Failed to generate cover image';
        let type = 'error';

        if (['FAILED', 'CANCELLED'].includes(taskStatus)) {
          shot.isCoverGenerated = true;
          shot.coverGenerateProgress = 0;
        }

        if (taskStatus === 'SUCCEEDED') {
          shot.isCoverGenerated = true;
          shot.coverGenerateProgress = 100;
          shot.cover = cover;

          message = 'Cover image generated successfully';
          type = 'success';
        }

        // @ts-ignore
        toastPool.insert(new Toast(message, type));
      },
    );
  }

  public projectAvatarUpdated(project: Project) {
    this.socket.emit('project-avatar-uploaded', project);
  }

  public addListener(event: string, listener: (...args: any[]) => void) {
    this.socket.on(event, listener);
  }
}

const appSocket = new AppSocket();

export { appSocket };
